import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FaCheckCircle } from "react-icons/fa";
import PaymentService from "services/PaymentService";
import { RouteName } from "constant/routeNames";
import { Helmet } from "react-helmet-async";
import { updateUserRole } from "redux/userRedux";

function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [subscription, setSubscription] = useState<any>(null);
  const [fetching, setFetching] = useState(true);

  const { planId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirect_status = queryParams.get("redirect_status");

  const fetchSubscription = async () => {
    try {
      const response = await PaymentService.getpurchasedSubscription(planId);
      console.log("Subscription fetched:", response);
      const subData = response?.data || response;
      setSubscription(subData);

      // Update user role to plus_subscription if subscription is active
      if (subData?.status === "active" && subData?.serviceId === "1") {
        dispatch(updateUserRole("plus_subscription"));
      }

      setFetching(false);
    } catch (error) {
      console.error("Error during fetching subscription:", error);
      setSubscription(null);
      setFetching(false);
    }
  };

  useEffect(() => {
    // Always fetch subscription when page loads
    fetchSubscription();
  }, []);

  // If payment failed, redirect back to subscription plan
  useEffect(() => {
    if (redirect_status === "failed") {
      navigate(RouteName.SubscriptionPlan);
    }
  }, [redirect_status, navigate]);

  const handleGoHome = () => {
    navigate("/");
  };

  const handleViewSubscription = () => {
    navigate(`/subscription/${RouteName.SubscriptionPlan}`); // → /subscription/plan
  };

  return (
    <>
      <Helmet>
        <title>
          {subscription?.plan_name
            ? `${subscription.plan_name} - Successfully Subscribed`
            : "Subscription Successful - AYOZAT"}
        </title>
        <meta
          name="description"
          content="Your subscription has been activated. Enjoy premium content on AYOZAT."
        />
        <meta property="og:title" content="Subscription Successful - AYOZAT" />
        <meta
          property="og:description"
          content="Your subscription has been activated. Enjoy premium content on AYOZAT."
        />
        <meta property="og:image" content="https://ayozat.co.uk/logo192.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Subscription Successful - AYOZAT" />
        <meta
          name="twitter:description"
          content="Your subscription has been activated. Enjoy premium content on AYOZAT."
        />
        <meta name="twitter:image" content="https://ayozat.co.uk/logo192.png" />
        <meta name="twitter:site" content="AYOZAT" />
      </Helmet>

      {!fetching && (
        <div className="mx-auto flex w-[80%] items-center justify-center max-lg:w-[95%] max-sm:mb-20 max-sm:block">
          <div className="relative w-full overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[#15151a] to-[#0c0c0f] p-10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] max-sm:p-6">
            {/* ambient glow */}
            <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-green-500/20 blur-3xl" />

            <div className="relative">
              {/* Success Icon */}
              <div className="mb-6 flex justify-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-green-500/10 ring-1 ring-green-500/30">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/15">
                    <FaCheckCircle className="text-4xl text-green-400 drop-shadow-[0_0_10px_rgba(74,222,128,0.6)]" />
                  </div>
                </div>
              </div>

              {/* Success Message */}
              <div className="mb-8 text-center">
                <h1 className="mb-3 text-[32px] font-bold text-white max-lg:text-[25px] max-sm:text-[22px]">
                  {subscription
                    ? `Thank you for subscribing to ${
                        subscription?.title || ""
                      } plan!`
                    : "Thank you for subscribing!"}
                </h1>
                <p className="mx-auto max-w-md text-[15px] leading-relaxed text-gray-400">
                  {subscription
                    ? `Your ${
                        subscription?.plan_name ||
                        subscription?.name ||
                        "subscription"
                      } has been activated successfully. You now have access to all premium content.`
                    : "Your subscription has been activated successfully. You now have access to all premium content."}
                </p>
              </div>

              {/* Subscription Details */}
              {subscription ? (
                <div className="mx-auto mb-8 max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm">
                  <h2 className="mb-4 text-[18px] font-semibold text-white">
                    {subscription?.plan_name ||
                      subscription?.name ||
                      "Subscription"}
                  </h2>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-[14px]">
                      <span className="text-gray-400">Status</span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 px-3 py-1 font-medium text-green-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
                        {subscription?.status || "Active"}
                      </span>
                    </div>
                    {subscription?.renewal_date && (
                      <div className="flex justify-between text-[14px]">
                        <span className="text-gray-400">Renewal Date</span>
                        <span className="text-gray-200">
                          {new Date(
                            subscription.renewal_date
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {subscription?.billing_cycle && (
                      <div className="flex justify-between text-[14px]">
                        <span className="text-gray-400">Billing Cycle</span>
                        <span className="text-gray-200">
                          {subscription.billing_cycle}
                        </span>
                      </div>
                    )}
                    <p className="border-t border-white/10 pt-3 text-[13px] text-gray-400">
                      Check your email for a confirmation and receipt details.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mx-auto mb-8 max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm">
                  <h2 className="mb-2 text-[18px] font-semibold text-white">
                    No subscriptions found
                  </h2>
                  <p className="text-[14px] text-gray-400">
                    If you believe this is an error, please contact our support
                    team or check your email for confirmation details.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-4 max-sm:flex-col">
                <button
                  className="button-red rounded-full px-7 py-3 text-center font-semibold text-white shadow-lg transition-opacity hover:opacity-90 max-sm:w-full"
                  onClick={handleGoHome}
                >
                  Start Watching
                </button>
                <button
                  className="rounded-full border border-white/20 px-7 py-3 text-center font-semibold text-gray-200 transition-colors hover:border-white/40 hover:bg-white/5 max-sm:w-full"
                  onClick={handleViewSubscription}
                >
                  View Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {fetching && (
        <div className="mx-auto h-96 w-[80%] animate-pulse rounded-3xl border border-white/10 bg-white/[0.03] max-lg:w-[95%] max-sm:mb-20 max-sm:block"></div>
      )}
    </>
  );
}

export default SubscriptionSuccessPage;